import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Button, TextField, MenuItem, Typography, IconButton, Stack, Switch,
  FormControlLabel, Paper, Divider, Alert,
} from '@mui/material';
import { Add, Delete, Save } from '@mui/icons-material';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import DataTableCard from '../../components/ui/DataTableCard';
import {
  getRestaurants,
  getRestaurantAddonGroups,
  saveRestaurantAddonGroups,
  type AddonGroupRow,
} from '../../services/businessService';

function emptyGroup(): AddonGroupRow {
  return {
    id: `ag_${Date.now()}`,
    name: '',
    minSelection: 0,
    maxSelection: 5,
    required: false,
    addons: [],
  };
}

function emptyAddon(groupName: string) {
  return {
    id: `ad_${Date.now()}`,
    name: '',
    price: 0,
    maxQty: 3,
    group: groupName,
  };
}

export default function AddonGroupsPage() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [restaurantId, setRestaurantId] = useState(searchParams.get('restaurantId') || '');
  const [groups, setGroups] = useState<AddonGroupRow[]>([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const rid = searchParams.get('restaurantId');
    if (rid) setRestaurantId(rid);
  }, [searchParams]);

  const { data: restaurants = [] } = useQuery({
    queryKey: ['restaurants-admin'],
    queryFn: () => getRestaurants(true),
  });

  const { data: serverGroups = [], isLoading } = useQuery({
    queryKey: ['addon-groups', restaurantId],
    queryFn: () => getRestaurantAddonGroups(restaurantId),
    enabled: !!restaurantId,
  });

  useEffect(() => {
    setGroups(serverGroups);
    setDirty(false);
  }, [serverGroups]);

  const saveMutation = useMutation({
    mutationFn: () => saveRestaurantAddonGroups(restaurantId, groups),
    onSuccess: (saved) => {
      setGroups(saved);
      setDirty(false);
      queryClient.invalidateQueries({ queryKey: ['addon-groups', restaurantId] });
    },
  });

  const updateGroup = (index: number, patch: Partial<AddonGroupRow>) => {
    setGroups((prev) => prev.map((g, i) => (i === index ? { ...g, ...patch } : g)));
    setDirty(true);
  };

  const updateAddon = (gi: number, ai: number, patch: Partial<AddonGroupRow['addons'][0]>) => {
    setGroups((prev) => prev.map((g, i) => {
      if (i !== gi) return g;
      const addons = g.addons.map((a, j) => (j === ai ? { ...a, ...patch } : a));
      return { ...g, addons };
    }));
    setDirty(true);
  };

  return (
    <PageShell>
      <PageHeader
        eyebrow="Food Delivery"
        title="Addon Groups"
        subtitle="Per-restaurant addon groups used in the Food Add wizard and customer customization."
        actions={(
          <Button
            variant="contained"
            startIcon={<Save />}
            disabled={!restaurantId || !dirty || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            Save Groups
          </Button>
        )}
      />

      <DataTableCard title="Restaurant Addon Configuration" subtitle="Groups like Toppings, Sides, Beverages — each with priced addons.">
        <TextField
          select
          label="Restaurant"
          value={restaurantId}
          onChange={(e) => setRestaurantId(e.target.value)}
          sx={{ mb: 2, minWidth: 320 }}
        >
          {restaurants.map((r: { _id: string; name: string }) => (
            <MenuItem key={r._id} value={r._id}>{r.name}</MenuItem>
          ))}
        </TextField>

        {!restaurantId && (
          <Alert severity="info">Select a restaurant to manage addon groups.</Alert>
        )}

        {restaurantId && isLoading && (
          <Typography variant="body2" color="text.secondary">Loading addon groups…</Typography>
        )}

        {restaurantId && !isLoading && (
          <Stack spacing={2}>
            {groups.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No addon groups yet. Add a group to define customization options for this restaurant.
              </Typography>
            )}

            {groups.map((group, gi) => (
              <Paper key={group.id || gi} variant="outlined" sx={{ p: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
                  <TextField
                    label="Group name"
                    value={group.name}
                    onChange={(e) => updateGroup(gi, { name: e.target.value })}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Min"
                    type="number"
                    value={group.minSelection ?? 0}
                    onChange={(e) => updateGroup(gi, { minSelection: Number(e.target.value) })}
                    sx={{ width: 90 }}
                  />
                  <TextField
                    label="Max"
                    type="number"
                    value={group.maxSelection ?? 5}
                    onChange={(e) => updateGroup(gi, { maxSelection: Number(e.target.value) })}
                    sx={{ width: 90 }}
                  />
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={Boolean(group.required)}
                        onChange={(e) => updateGroup(gi, { required: e.target.checked })}
                      />
                    )}
                    label="Required"
                  />
                  <IconButton
                    color="error"
                    onClick={() => { setGroups((prev) => prev.filter((_, i) => i !== gi)); setDirty(true); }}
                  >
                    <Delete />
                  </IconButton>
                </Stack>

                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Addons</Typography>
                <Stack spacing={1}>
                  {group.addons.map((addon, ai) => (
                    <Stack key={addon.id || ai} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                      <TextField
                        label="Addon name"
                        value={addon.name}
                        onChange={(e) => updateAddon(gi, ai, { name: e.target.value })}
                        sx={{ flex: 1 }}
                        size="small"
                      />
                      <TextField
                        label="Price ₹"
                        type="number"
                        value={addon.price}
                        onChange={(e) => updateAddon(gi, ai, { price: Number(e.target.value) })}
                        sx={{ width: 120 }}
                        size="small"
                      />
                      <TextField
                        label="Max qty"
                        type="number"
                        value={addon.maxQty ?? 3}
                        onChange={(e) => updateAddon(gi, ai, { maxQty: Number(e.target.value) })}
                        sx={{ width: 100 }}
                        size="small"
                      />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setGroups((prev) => prev.map((g, i) => (
                            i === gi ? { ...g, addons: g.addons.filter((_, j) => j !== ai) } : g
                          )));
                          setDirty(true);
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>

                <Button
                  size="small"
                  startIcon={<Add />}
                  sx={{ mt: 1 }}
                  onClick={() => {
                    setGroups((prev) => prev.map((g, i) => (
                      i === gi ? { ...g, addons: [...g.addons, emptyAddon(g.name)] } : g
                    )));
                    setDirty(true);
                  }}
                >
                  Add addon
                </Button>
              </Paper>
            ))}

            <Divider />

            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => { setGroups((prev) => [...prev, emptyGroup()]); setDirty(true); }}
            >
              Add group
            </Button>

            {saveMutation.isError && (
              <Alert severity="error">Failed to save addon groups. Check your connection and try again.</Alert>
            )}
            {saveMutation.isSuccess && !dirty && (
              <Alert severity="success">Addon groups saved.</Alert>
            )}
          </Stack>
        )}
      </DataTableCard>
    </PageShell>
  );
}
